import { User } from '@maxiron/shared';

// In production (via Docker build arg), VITE_API_URL is set to empty string to use relative paths
// In development, it defaults to localhost:3000
const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';
const DEFAULT_REQUEST_TIMEOUT_MS = resolveDefaultRequestTimeoutMs();
const DEFAULT_AI_WORKSPACE_ID = 'maxiron';
const AI_WORKSPACE_STORAGE_KEY = 'ai.activeWorkspaceId';

function resolveDefaultRequestTimeoutMs(): number {
  const fromEnv = Number(import.meta.env.VITE_API_TIMEOUT_MS);
  if (Number.isFinite(fromEnv) && fromEnv > 0) return fromEnv;
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 120_000;
  }
  return 30_000;
}

function monitoringRequestTimeoutMs(): number {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 90_000;
  }
  return 10_000;
}

export type ManagedClusterKind = 'openshift' | 'kubernetes';

export interface ServiceReportItem {
  objectName: string;
  month: string;
  status: 'draft' | 'published';
  updatedAt: string;
}

export interface TenantReportGroup {
  tenantCode: string;
  reports: ServiceReportItem[];
}

export function managedClusterBase(kind?: ManagedClusterKind): string {
  return kind === 'kubernetes' ? '/api/kubernetes/clusters' : '/api/openshift/clusters';
}

let activeAiWorkspaceId = localStorage.getItem(AI_WORKSPACE_STORAGE_KEY) || DEFAULT_AI_WORKSPACE_ID;

export function setActiveAiWorkspaceId(workspaceId: string): void {
  const normalized = String(workspaceId || '').trim().toLowerCase() || DEFAULT_AI_WORKSPACE_ID;
  activeAiWorkspaceId = normalized;
  localStorage.setItem(AI_WORKSPACE_STORAGE_KEY, normalized);
}

export function getActiveAiWorkspaceId(): string {
  return activeAiWorkspaceId || DEFAULT_AI_WORKSPACE_ID;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async postStream<T>(
    endpoint: string,
    body: unknown,
    onEvent: (event: T) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const accessToken = localStorage.getItem('oci_access_token');
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    const workspaceAwarePath = endpoint.startsWith('/api/admin/ai-config')
      || endpoint.startsWith('/api/admin/ai-onboarding')
      || endpoint.startsWith('/api/ai-workflows')
      || endpoint.startsWith('/api/ai-learning');
    if (workspaceAwarePath) {
      headers['x-workspace-id'] = getActiveAiWorkspaceId();
    }
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(body),
      signal,
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error('Unauthorized');
      }
      const errorText = (await response.text().catch(() => '')).trim();
      throw new Error(
        errorText
          ? `Stream request failed with status ${response.status}: ${errorText}`
          : `Stream request failed with status ${response.status}`,
      );
    }
    if (!response.body) {
      throw new Error(`Stream request failed with status ${response.status}: empty response body`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        onEvent(JSON.parse(trimmed) as T);
      }
    }
    const remaining = buffer.trim();
    if (remaining) {
      onEvent(JSON.parse(remaining) as T);
    }
  }

  private async getStream<T>(
    endpoint: string,
    onEvent: (event: T) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const headers: Record<string, string> = {};
    const accessToken = localStorage.getItem('oci_access_token');
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    const workspaceAwarePath = endpoint.startsWith('/api/admin/ai-config')
      || endpoint.startsWith('/api/admin/ai-onboarding')
      || endpoint.startsWith('/api/ai-workflows')
      || endpoint.startsWith('/api/ai-learning');
    if (workspaceAwarePath) {
      headers['x-workspace-id'] = getActiveAiWorkspaceId();
    }
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'GET',
      headers,
      credentials: 'include',
      signal,
    });
    if (!response.ok || !response.body) {
      if (response.status === 401 || response.status === 403) {
        throw new Error('Unauthorized');
      }
      throw new Error(`Stream request failed with status ${response.status}`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        onEvent(JSON.parse(trimmed) as T);
      }
    }
    const remaining = buffer.trim();
    if (remaining) {
      onEvent(JSON.parse(remaining) as T);
    }
  }

  private async getWithTimeout<T>(endpoint: string, timeoutMs: number): Promise<T> {
    const ms = Math.max(0, Number(timeoutMs) || 0);
    if (ms <= 0) return this.get<T>(endpoint);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), ms);
    try {
      // Passing a signal disables the default 30s timeout in `request()`.
      return await this.request<T>(endpoint, { method: 'GET', signal: controller.signal });
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      ...options.headers as Record<string, string>,
    };
    
    // Azure AD BFF auth: Session managed via HttpOnly cookies
    // No Authorization header needed - credentials are sent automatically
    
    // Legacy OCI auth: Add Authorization header if access token exists (backward compatibility)
    const accessToken = localStorage.getItem('oci_access_token');
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    // Only set Content-Type if there's a body
    if (options.body) {
      headers['Content-Type'] = 'application/json';
    }

    const workspaceAwarePath = endpoint.startsWith('/api/admin/ai-config')
      || endpoint.startsWith('/api/admin/ai-onboarding')
      || endpoint.startsWith('/api/ai-workflows')
      || endpoint.startsWith('/api/ai-learning');
    if (workspaceAwarePath && !headers['x-workspace-id']) {
      headers['x-workspace-id'] = getActiveAiWorkspaceId();
    }
    
    const controller = options.signal ? null : new AbortController();
    const timeoutId =
      controller == null
        ? null
        : window.setTimeout(() => controller.abort(), DEFAULT_REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include', // Include cookies for session (Azure auth)
        signal: options.signal ?? controller?.signal,
      });

      const effectiveUrl = String((response as any)?.url || url);
      const looksLikeAuthRedirect =
        response.redirected &&
        (effectiveUrl.includes('/login') ||
          effectiveUrl.includes('/api/azure-auth') ||
          effectiveUrl.includes('/api/auth/login'));

      // If we were redirected to login/auth, this is NOT a server outage.
      if (looksLikeAuthRedirect) {
        throw new Error('Unauthorized');
      }

      const contentType = response.headers.get('content-type') || '';

      // If backend (or ingress) is returning 5xx, treat as connection issue so UI flips to maintenance mode.
      // This covers the case where ingress returns a 503 HTML page while backend is down.
      // BUT: if the backend itself returned a JSON 5xx with `{ error: ... }`, that is a normal
      // application error (e.g. an upstream Cursor/OpenAI failure) and must not blank the portal.
      if (response.status >= 500) {
        if (contentType.includes('application/json')) {
          const error = await response.json().catch(() => ({ error: 'Request failed' }));
          throw new Error(error.error || `HTTP ${response.status}`);
        }
        console.warn(`API returned ${response.status} - backend likely unavailable`);
        window.dispatchEvent(new CustomEvent('maxiron:server-connection-error'));
        throw new Error('SERVER_CONNECTION_ERROR');
      }

      if (contentType.includes('text/html')) {
        // If this is HTML from an auth flow (login page), treat as Unauthorized.
        if (
          effectiveUrl.includes('/login') ||
          effectiveUrl.includes('/api/azure-auth') ||
          effectiveUrl.includes('/api/auth/login')
        ) {
          throw new Error('Unauthorized');
        }
        console.warn('Backend returned HTML (likely ingress error page / maintenance)');
        window.dispatchEvent(new CustomEvent('maxiron:server-connection-error'));
        throw new Error('SERVER_CONNECTION_ERROR');
      }

      if (!response.ok) {
        // Auth errors should not flip UI to maintenance.
        if (response.status === 401 || response.status === 403) {
          throw new Error('Unauthorized');
        }
        const error = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      // Handle 204 No Content or empty responses
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return {} as T;
      }

      return response.json();
    } catch (error: any) {
      // Handle request timeouts
      if (error?.name === 'AbortError') {
        // This AbortError is triggered by our own client-side timeout.
        // Treat it as a per-request timeout (not as a global server outage signal),
        // otherwise one slow endpoint/tenant can flip the whole UI into maintenance mode.
        throw new Error('REQUEST_TIMEOUT');
      }

      // Handle JSON parse errors (backend returned HTML with 200 status)
      if (error instanceof SyntaxError && error.message.includes('JSON')) {
        console.warn('Failed to parse JSON response - backend likely down or returning HTML');
        window.dispatchEvent(new CustomEvent('maxiron:server-connection-error'));
        throw new Error('SERVER_CONNECTION_ERROR');
      }
      
      // Handle network errors (backend down, CORS, etc.)
      if (error instanceof TypeError && (error.message === 'Failed to fetch' || error.message.includes('NetworkError'))) {
        // Broadcast a global connection-issue event so the UI can show a friendly screen
        // regardless of which page initiated the request.
        window.dispatchEvent(new CustomEvent('maxiron:server-connection-error'));
        throw new Error('SERVER_CONNECTION_ERROR');
      }
      throw error;
    } finally {
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
    }
  }

  async get<T>(
    endpoint: string,
    options?: { timeoutMs?: number; signal?: AbortSignal },
  ): Promise<T> {
    if (options?.timeoutMs || options?.signal) {
      const controller = new AbortController();
      let tid: ReturnType<typeof window.setTimeout> | null = null;
      const abort = () => controller.abort();
      if (options.timeoutMs) {
        tid = window.setTimeout(abort, options.timeoutMs);
      }
      if (options.signal) {
        if (options.signal.aborted) abort();
        else options.signal.addEventListener('abort', abort, { once: true });
      }
      try {
        return await this.request<T>(endpoint, { method: 'GET', signal: controller.signal });
      } finally {
        if (tid != null) window.clearTimeout(tid);
      }
    }
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(
    endpoint: string,
    data?: any,
    options?: { timeoutMs?: number; signal?: AbortSignal },
  ): Promise<T> {
    const reqInit: RequestInit = {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    };
    if (options?.timeoutMs || options?.signal) {
      const controller = new AbortController();
      let tid: ReturnType<typeof window.setTimeout> | null = null;
      const abort = () => controller.abort();
      if (options.timeoutMs) {
        tid = window.setTimeout(abort, options.timeoutMs);
      }
      if (options.signal) {
        if (options.signal.aborted) abort();
        else options.signal.addEventListener('abort', abort, { once: true });
      }
      try {
        return await this.request<T>(endpoint, { ...reqInit, signal: controller.signal });
      } finally {
        if (tid != null) window.clearTimeout(tid);
      }
    }
    return this.request<T>(endpoint, reqInit);
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Auth endpoints
  async getMe(): Promise<{ user: User; accessibleTenants: string[] }> {
    return this.get('/api/auth/me');
  }

  async getPermissionSchema(): Promise<{ schema: any }> {
    return this.get('/api/admin/permission-schema');
  }

  async updatePreferences(preferences: Partial<User['preferences']>): Promise<{ preferences: User['preferences'] }> {
    return this.put('/api/auth/preferences', preferences);
  }

  async logout(): Promise<void> {
    return this.post('/api/auth/logout');
  }

  // Cloud endpoints
  async getCloudSummary(tenantCode: string): Promise<any> {
    return this.get(`/api/cloud/${tenantCode}/summary`);
  }

  // Tenant endpoints
  async getTenants(): Promise<any> {
    return this.get('/api/tenants');
  }

  async getTenant(id: string): Promise<any> {
    return this.get(`/api/tenants/${id}`);
  }

  async getTenantByCode(code: string): Promise<any> {
    return this.get(`/api/tenants/code/${code}`);
  }

  async getTenantOptions(
    code: string,
    source: string,
    extraParams: Record<string, string> = {},
  ): Promise<{ options: string[] }> {
    const qs = new URLSearchParams({ source, ...extraParams });
    return this.get(`/api/tenants/${encodeURIComponent(code)}/options?${qs}`);
  }

  async getTenantYaml(code: string): Promise<{yaml: string, code: string}> {
    return this.get(`/api/tenants/${code}/yaml`);
  }

  async getTenantTemplateYaml(): Promise<{yaml: string}> {
    return this.get('/api/tenants/template/yaml');
  }

  async updateTenantYaml(code: string, yaml: string): Promise<any> {
    return this.put(`/api/tenants/${code}/yaml`, { yaml });
  }

  async uploadTenantLogo(code: string, imageBase64: string, contentType: string): Promise<{ logoUrl?: string }> {
    return this.put(`/api/tenants/code/${encodeURIComponent(code)}/logo`, { imageBase64, contentType });
  }

  async deleteTenantLogo(code: string): Promise<void> {
    await this.delete(`/api/tenants/code/${encodeURIComponent(code)}/logo`);
  }

  async createTenantYaml(code: string, yaml: string): Promise<any> {
    return this.post('/api/tenants/yaml', { code, yaml });
  }

  async getMasterYaml(): Promise<{yaml: string}> {
    return this.get('/api/tenants/master/yaml');
  }

  async updateMasterYaml(yaml: string): Promise<any> {
    return this.put('/api/tenants/master/yaml', { yaml });
  }

  async refreshTenantCache(): Promise<any> {
    return this.post('/api/tenants/refresh');
  }

  async saveVaultSecret(key: string, value: string): Promise<any> {
    return this.put('/api/vault/secrets', { key, value });
  }

  async checkVaultSecret(key: string): Promise<any> {
    return this.get(`/api/vault/secrets/${key}`);
  }

  async createTenant(data: { name: string; code: string; description?: string }): Promise<any> {
    return this.post('/api/tenants', data);
  }

  async updateTenant(id: string, data: { name?: string; description?: string }): Promise<any> {
    return this.patch(`/api/tenants/${id}`, data);
  }

  async deleteTenant(id: string): Promise<void> {
    return this.delete(`/api/tenants/${id}`);
  }

  async getInstances(tenantCode: string, provider?: string): Promise<any> {
    const query = provider ? `?provider=${provider}` : '';
    return this.get(`/api/cloud/${tenantCode}/instances${query}`);
  }

  async getDevInstances(): Promise<any> {
    return this.get('/api/dev/instances');
  }

  async getPRInstances(): Promise<any> {
    return this.get('/api/pr/instances');
  }

  async getDevImages(): Promise<any> {
    return this.get('/api/dev/images');
  }

  async getOpenshiftClusters(kind?: ManagedClusterKind): Promise<any> {
    return this.get(managedClusterBase(kind));
  }

  async startOpenshiftCluster(tenantCode: string, instanceName: string, cloudProvider: string, kind?: ManagedClusterKind): Promise<{ status: string; runId: string; workflowName: string }> {
    return this.post(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/start`, {});
  }

  async stopOpenshiftCluster(tenantCode: string, instanceName: string, cloudProvider: string, kind?: ManagedClusterKind): Promise<{ status: string; runId: string; workflowName: string }> {
    return this.post(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/stop`, {});
  }

  async getOpenshiftClusterSchedules(tenantCode: string, instanceName: string, cloudProvider: string, kind?: ManagedClusterKind): Promise<{ schedules: any[] }> {
    return this.get(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/schedules`);
  }

  async createOpenshiftClusterSchedule(
    tenantCode: string,
    instanceName: string,
    cloudProvider: string,
    payload: { action: 'start' | 'stop'; cronExpression: string },
    kind?: ManagedClusterKind,
  ): Promise<any> {
    return this.post(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/schedules`, payload);
  }

  async deleteOpenshiftClusterSchedule(
    tenantCode: string,
    instanceName: string,
    cloudProvider: string,
    scheduleId: string,
    kind?: ManagedClusterKind,
  ): Promise<{ ok: boolean }> {
    return this.delete(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/schedules/${encodeURIComponent(scheduleId)}`);
  }

  async checkClusterHealth(tenantCode: string, instanceName: string, cloudProvider: string, kind?: ManagedClusterKind): Promise<any> {
    return this.get(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/health`);
  }

  async getClusterStatus(tenantCode: string, instanceName: string, cloudProvider: string, kind?: ManagedClusterKind): Promise<any> {
    return this.get(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/status`);
  }

  async getClusterMetrics(tenantCode: string, instanceName: string, cloudProvider: string, kind?: ManagedClusterKind): Promise<any> {
    return this.get(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/metrics`);
  }

  // OpenShift Secrets Management
  async getOpenshiftNamespaces(tenantCode: string, instanceName: string, cloudProvider: string, kind?: ManagedClusterKind): Promise<any> {
    return this.get(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/namespaces`);
  }

  // OpenShift Logs & Events
  async getOpenshiftNamespacesAll(tenantCode: string, instanceName: string, cloudProvider: string, kind?: ManagedClusterKind): Promise<any> {
    return this.get(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/namespaces/all`);
  }

  async getOpenshiftPods(tenantCode: string, instanceName: string, cloudProvider: string, namespace: string, kind?: ManagedClusterKind): Promise<any> {
    return this.get(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/namespaces/${namespace}/pods`);
  }

  async getOpenshiftPodEvents(
    tenantCode: string,
    instanceName: string,
    cloudProvider: string,
    namespace: string,
    pod: string,
    kind?: ManagedClusterKind,
  ): Promise<any> {
    return this.get(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/namespaces/${namespace}/pods/${pod}/events`);
  }

  async deleteOpenshiftPod(
    tenantCode: string,
    instanceName: string,
    cloudProvider: string,
    namespace: string,
    pod: string,
    kind?: ManagedClusterKind,
  ): Promise<{ success: boolean; pod: string; namespace: string }> {
    return this.delete(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/namespaces/${namespace}/pods/${pod}`);
  }

  async getOpenshiftPodLogs(
    tenantCode: string,
    instanceName: string,
    cloudProvider: string,
    namespace: string,
    pod: string,
    kind?: ManagedClusterKind,
  ): Promise<string> {
    const response = await fetch(`${this.baseUrl}${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/namespaces/${namespace}/pods/${pod}/logs`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.text();
  }

  async getOpenshiftSecrets(tenantCode: string, instanceName: string, cloudProvider: string, namespace: string, kind?: ManagedClusterKind): Promise<any> {
    return this.get(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/namespaces/${namespace}/secrets`);
  }

  async getOpenshiftRouteUrl(
    tenantCode: string,
    instanceName: string,
    cloudProvider: string,
    namespace: string,
    routeName: string,
    kind?: ManagedClusterKind,
  ): Promise<{ url: string | null }> {
    return this.get(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/namespaces/${namespace}/routes/${routeName}/url`);
  }

  async saveOpenshiftSecret(
    tenantCode: string,
    instanceName: string,
    cloudProvider: string,
    namespace: string,
    secretName: string,
    secretType: string,
    data: Record<string, string>,
    kind?: ManagedClusterKind,
  ): Promise<any> {
    return this.post(`${managedClusterBase(kind)}/${tenantCode}/${instanceName}/${cloudProvider}/namespaces/${namespace}/secrets`, {
      name: secretName,
      type: secretType,
      data,
    });
  }

  // MAS Instances
  async getMASInstances(): Promise<any> {
    return this.get('/api/mas/instances');
  }

  async getServerContainers(serverId: string): Promise<any> {
    return this.get(`/api/dev/instances/${encodeURIComponent(serverId)}/containers`);
  }

  async getContainerLogs(serverId: string, containerName: string): Promise<string> {
    const response = await fetch(`${this.baseUrl}/api/dev/instances/${encodeURIComponent(serverId)}/containers/${encodeURIComponent(containerName)}/logs`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.text();
  }

  async getContainerUrl(serverId: string, containerName: string): Promise<any> {
    return this.get(`/api/dev/instances/${encodeURIComponent(serverId)}/containers/${encodeURIComponent(containerName)}/url`);
  }

  async restartContainer(serverId: string, containerName: string): Promise<{ success: boolean; containerName: string }> {
    return this.post(
      `/api/dev/instances/${encodeURIComponent(serverId)}/containers/${encodeURIComponent(containerName)}/restart`,
      undefined,
      { timeoutMs: 120_000 },
    );
  }

  async getRebuildLog(serverId: string): Promise<string> {
    const response = await fetch(`${this.baseUrl}/api/dev/instances/${encodeURIComponent(serverId)}/rebuild-log`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.text();
  }

  async rebuildDevInstance(serverId: string, tenantCode?: string): Promise<{ success: boolean; message?: string }> {
    return this.post(`/api/dev/instances/${encodeURIComponent(serverId)}/rebuild`, tenantCode ? { tenantCode } : undefined);
  }

  async destroyInstance(serverName: string): Promise<{ runId?: string; canDestroy: boolean; reason?: string }> {
    return this.post(`/api/instances/${encodeURIComponent(serverName)}/destroy`, {});
  }

  async deletePRInstance(serverId: string): Promise<any> {
    return this.delete(`/api/dev/instances/${encodeURIComponent(serverId)}`);
  }

  async getClusters(tenantCode: string): Promise<any> {
    return this.get(`/api/cloud/${tenantCode}/clusters`);
  }

  async getCosts(tenantCode: string, days?: number, startDate?: Date, endDate?: Date): Promise<any> {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.append('startDate', startDate.toISOString());
      params.append('endDate', endDate.toISOString());
    } else if (days) {
      params.append('days', days.toString());
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.get(`/api/cloud/${tenantCode}/costs${query}`);
  }

  // Hetzner environment endpoints
  async getHetznerDevEnvironments(): Promise<any> {
    return this.get('/api/hetzner-envs/dev');
  }

  async getHetznerInfrastructure(): Promise<any> {
    return this.get('/api/hetzner-envs/infrastructure');
  }

  async getHetznerProduction(tenantCode: string): Promise<any> {
    return this.get(`/api/hetzner-envs/production/${tenantCode}`);
  }

  async getAllHetznerEnvironments(): Promise<any> {
    return this.get('/api/hetzner-envs/all');
  }

  // Portal config (public, no auth required)
  async getPortalConfig(): Promise<{ argoUiUrl: string; rancherUiUrl: string | null; identityDriver?: string }> {
    return this.get('/api/portal-config');
  }

  async getIdentityProviders(): Promise<{ idps: Array<{
    alias: string;
    displayName: string;
    providerType: 'azure-entra' | 'oidc' | 'saml';
    enabled: boolean;
    tenantId?: string;
    clientId?: string;
    clientSecret?: string;
    discoveryUrl?: string;
  }> }> {
    return this.get('/api/admin/identity-providers');
  }

  async createIdentityProvider(body: {
    alias: string;
    displayName: string;
    providerType: 'azure-entra' | 'oidc' | 'saml';
    tenantId?: string;
    clientId?: string;
    clientSecret?: string;
    discoveryUrl?: string;
  }): Promise<{ success: boolean }> {
    return this.post('/api/admin/identity-providers', body);
  }

  async updateIdentityProvider(alias: string, body: {
    displayName?: string;
    tenantId?: string;
    clientId?: string;
    clientSecret?: string;
    discoveryUrl?: string;
    enabled?: boolean;
  }): Promise<{ success: boolean }> {
    return this.put(`/api/admin/identity-providers/${encodeURIComponent(alias)}`, body);
  }

  async deleteIdentityProvider(alias: string): Promise<{ success: boolean }> {
    return this.delete(`/api/admin/identity-providers/${encodeURIComponent(alias)}`);
  }

  // Config endpoints
  async getAvailableTenants(): Promise<{ tenants: string[] }> {
    return this.get('/api/configs/tenants');
  }

  // Vault Secret Management
  async listVaultSecrets(): Promise<{ secrets: Array<{ key: string }> }> {
    return this.get('/api/vault/secrets');
  }

  async deleteVaultSecret(key: string): Promise<void> {
    return this.delete(`/api/vault/secrets/${encodeURIComponent(key)}`);
  }

  // Monitoring (polling-backed)
  async getMonitoringCurrent(tenantCode: string): Promise<any> {
    return this.getWithTimeout(
      `/api/monitoring/tenants/${encodeURIComponent(tenantCode)}/current`,
      monitoringRequestTimeoutMs(),
    );
  }

  async getMonitoringAlerts(tenantCode: string): Promise<any> {
    return this.getWithTimeout(
      `/api/monitoring/tenants/${encodeURIComponent(tenantCode)}/alerts`,
      monitoringRequestTimeoutMs(),
    );
  }

  async getMonitoringAlarms(tenantCode: string): Promise<any> {
    return this.getWithTimeout(
      `/api/monitoring/tenants/${encodeURIComponent(tenantCode)}/alarms`,
      monitoringRequestTimeoutMs(),
    );
  }

  async getServiceHealthMonthly(tenantCode: string, month?: string): Promise<any> {
    const q = month ? `?month=${encodeURIComponent(month)}` : '';
    return this.get(`/api/monitoring/tenants/${encodeURIComponent(tenantCode)}/service-health/monthly${q}`);
  }

  // Dashboard
  async getDashboardHealth(tenantsCsv: string): Promise<{ status: string; alarms: number; alerts: number; tenants: number }> {
    return this.get(`/api/dashboard/health?tenants=${encodeURIComponent(tenantsCsv)}`);
  }

  async getDashboardJiraNext(tenantsCsv: string): Promise<any> {
    return this.get(`/api/dashboard/jira-next?tenants=${encodeURIComponent(tenantsCsv)}`);
  }

  async getDashboardJiraLastTouched(tenantsCsv: string): Promise<any> {
    return this.get(`/api/dashboard/jira-last-touched?tenants=${encodeURIComponent(tenantsCsv)}`);
  }

  async getDashboardJiraPrioritySummary(tenantsCsv: string): Promise<any> {
    return this.get(`/api/dashboard/jira-priority-summary?tenants=${encodeURIComponent(tenantsCsv)}`);
  }

  async getDashboardAlertsSummary(tenantsCsv: string): Promise<any> {
    return this.get(`/api/dashboard/alerts-summary?tenants=${encodeURIComponent(tenantsCsv)}`);
  }

  // Monitoring admin config (edits OCI YAML monitoring subtree)
  async getMonitoringMasterConfig(): Promise<any> {
    return this.get('/api/monitoring/config/master');
  }

  async updateMonitoringMasterConfig(monitoring: any): Promise<any> {
    return this.put('/api/monitoring/config/master', { monitoring });
  }

  async getMonitoringTenantConfig(tenantCode: string): Promise<any> {
    return this.get(`/api/monitoring/config/tenant/${encodeURIComponent(tenantCode)}`);
  }

  async updateMonitoringTenantConfig(tenantCode: string, monitoring: any): Promise<any> {
    return this.put(`/api/monitoring/config/tenant/${encodeURIComponent(tenantCode)}`, { monitoring });
  }

  // Approvals admin config (edits OCI YAML approvals subtree)
  async getApprovalsMasterConfig(): Promise<any> {
    return this.get('/api/approvals/config/master');
  }

  async updateApprovalsMasterConfig(approvals: any): Promise<any> {
    return this.put('/api/approvals/config/master', { approvals });
  }

  async getApprovalsTenantConfig(tenantCode: string): Promise<any> {
    return this.get(`/api/approvals/config/tenant/${encodeURIComponent(tenantCode)}`);
  }

  async updateApprovalsTenantConfig(tenantCode: string, approvals: any): Promise<any> {
    return this.put(`/api/approvals/config/tenant/${encodeURIComponent(tenantCode)}`, { approvals });
  }

  async getApprovalsTenantConsentUrl(tenantCode: string): Promise<{ url: string | null; error?: string }> {
    return this.get(`/api/approvals/config/tenant/${encodeURIComponent(tenantCode)}/consent-url`);
  }

  // Approvals Graph API consent request tracking (email + pending/granted)
  async getApprovalsGraphApiStatus(tenantCode: string): Promise<{ tenantCode: string; graphApi: any }> {
    return this.get(`/api/approvals/graph-api-status/${encodeURIComponent(tenantCode)}`);
  }

  async requestApprovalsGraphApiAccess(tenantCode: string, adminEmail: string, domain?: string): Promise<any> {
    return this.post(`/api/approvals/graph-api-request/${encodeURIComponent(tenantCode)}`, { adminEmail, domain });
  }

  async approveApprovalsGraphApiAccess(
    tenantCode: string,
    credentials: { clientId: string; clientSecret: string; tenantId: string },
    domain?: string
  ): Promise<any> {
    return this.post(`/api/approvals/graph-api-approve/${encodeURIComponent(tenantCode)}`, { ...credentials, domain });
  }

  // Monitoring sync (push YAML -> tools)
  async syncKumaMonitorMap(tenantCode: string): Promise<any> {
    return this.post(`/api/monitoring/sync/kuma/${encodeURIComponent(tenantCode)}`, {});
  }

  async syncSyntheticSuites(tenantCode: string): Promise<any> {
    return this.post(`/api/monitoring/sync/synthetic/${encodeURIComponent(tenantCode)}`, {});
  }

  // Workflow operations
  async getWorkflows(): Promise<any[]> {
    return this.get('/api/workflows');
  }

  async getWorkflow(name: string): Promise<any> {
    return this.get(`/api/workflows/${encodeURIComponent(name)}`);
  }

  async triggerWorkflow(
    name: string, 
    parameters: Record<string, any>, 
    tenantCode?: string,
    approvalOptions?: { notifyMode?: 'select' | 'all'; selectedApprovers?: string[] }
  ): Promise<any> {
    // Keep wizard params inside `parameters`. Only hoist fields the trigger route reads at body root.
    const {
      configOverrides,
      tenantCode: paramTenantCode,
      ...workflowParameters
    } = parameters;

    return this.post(`/api/workflows/${encodeURIComponent(name)}/trigger`, {
      parameters: workflowParameters,
      tenantCode: paramTenantCode || tenantCode,
      ...(configOverrides !== undefined && { configOverrides }),
      ...approvalOptions,
    });
  }

  async getEligibleApprovers(workflowName: string): Promise<Array<{ email: string; name: string }>> {
    return this.get(`/api/workflows/${encodeURIComponent(workflowName)}/eligible-approvers`);
  }

  async getWorkflowDefaultConfig(
    workflowName: string,
    params: Record<string, string> = {},
  ): Promise<{ yaml: string; base64: string; preview: Record<string, unknown> }> {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null)),
    );
    return this.get(`/api/workflows/${encodeURIComponent(workflowName)}/default-config?${qs}`);
  }

  async getWorkflowRuns(filters?: { workflowName?: string; tenantCode?: string; status?: string; limit?: number }): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters?.workflowName) params.append('workflowName', filters.workflowName);
    if (filters?.tenantCode) params.append('tenantCode', filters.tenantCode);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.get(`/api/workflow-runs${query}`);
  }

  async getWorkflowRunsCount(): Promise<number> {
    const res = await this.get<{ count: number }>('/api/workflow-runs/count');
    return Number(res?.count) || 0;
  }

  async getWorkflowRunsArchive(filters: {
    from: string;
    to?: string;
    workflowName?: string;
    tenantCode?: string;
    status?: string;
    limit?: number;
  }): Promise<any[]> {
    const params = new URLSearchParams();
    params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.workflowName) params.append('workflowName', filters.workflowName);
    if (filters.tenantCode) params.append('tenantCode', filters.tenantCode);
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit.toString());
    return this.get(`/api/workflow-runs/archive?${params.toString()}`);
  }

  async getServiceReports(tenants: string[]): Promise<TenantReportGroup[]> {
    const params = new URLSearchParams();
    if (tenants.length > 0) params.set('tenants', tenants.join(','));
    const response = await this.get<{ groups: TenantReportGroup[] }>(`/api/service-reports?${params.toString()}`);
    return response.groups || [];
  }

  async getServiceReportContent(objectName: string): Promise<string> {
    const params = new URLSearchParams({ objectName });
    const response = await this.get<{ content: string }>(`/api/service-reports/content?${params.toString()}`);
    return response.content || '';
  }

  async updateServiceReportContent(objectName: string, content: string): Promise<void> {
    await this.put('/api/service-reports/content', { objectName, content });
  }

  async publishServiceReport(objectName: string): Promise<void> {
    await this.post('/api/service-reports/publish', { objectName });
  }

  async downloadServiceReport(objectName: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    const accessToken = localStorage.getItem('oci_access_token');
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    const params = new URLSearchParams({ objectName });
    const response = await fetch(`${this.baseUrl}/api/service-reports/download?${params.toString()}`, {
      method: 'GET',
      headers,
      credentials: 'include',
      signal: AbortSignal.timeout(180_000),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.blob();
  }

  async getAiRunsArchive(filters: {
    from: string;
    to?: string;
    streamId?: string;
    status?: string;
    limit?: number;
  }): Promise<{ runs: any[]; total: number }> {
    const params = new URLSearchParams();
    params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.streamId) params.append('streamId', filters.streamId);
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit.toString());
    return this.get(`/api/ai-workflows/runs/archive?${params.toString()}`);
  }

  async getAiActivityArchive(filters: {
    from: string;
    to?: string;
    streamId?: string;
    type?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ entries: any[]; total: number }> {
    const params = new URLSearchParams();
    params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.streamId) params.append('streamId', filters.streamId);
    if (filters.type) params.append('type', filters.type);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());
    return this.get(`/api/ai-workflows/activity/archive?${params.toString()}`);
  }

  async getAiHumanInputsArchive(filters: {
    from: string;
    to?: string;
    streamId?: string;
    status?: string;
    limit?: number;
  }): Promise<{ inputs: any[]; total: number }> {
    const params = new URLSearchParams();
    params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.streamId) params.append('streamId', filters.streamId);
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit.toString());
    return this.get(`/api/ai-workflows/human-inputs/archive?${params.toString()}`);
  }

  async getWorkflowRun(runId: string): Promise<any> {
    return this.get(`/api/workflow-runs/${encodeURIComponent(runId)}`);
  }

  async cancelWorkflowRun(runId: string): Promise<any> {
    return this.delete(`/api/workflow-runs/${encodeURIComponent(runId)}`);
  }

  async getWorkflowRunLogs(runId: string): Promise<any> {
    return this.get(`/api/workflow-runs/${encodeURIComponent(runId)}/logs`);
  }

  async getWorkflowRunLogSteps(runId: string): Promise<any> {
    return this.get(`/api/workflow-runs/${encodeURIComponent(runId)}/logs/steps`);
  }

  streamWorkflowLogs(runId: string): EventSource {
    const url = `${this.baseUrl}/api/workflow-runs/${encodeURIComponent(runId)}/logs`;
    return new EventSource(url, { withCredentials: true });
  }

  async getWorkflowSchedules(): Promise<any[]> {
    return this.get('/api/workflow-schedules');
  }

  async getWorkflowSchedulesCount(): Promise<number> {
    const res = await this.get<{ count: number }>('/api/workflow-schedules/count');
    return Number(res?.count) || 0;
  }

  async createWorkflowSchedule(schedule: { workflowName: string; parameters: Record<string, any>; schedule: string }): Promise<any> {
    const { workflowName, parameters, schedule: cronExpression } = schedule;

    // Keep wizard params inside `parameters`. Only hoist fields the schedule route reads at body root.
    const {
      configOverrides,
      tenantCode: paramTenantCode,
      ...workflowParameters
    } = parameters;

    const tenantCode = paramTenantCode || 'portal';

    return this.post(`/api/workflows/${encodeURIComponent(workflowName)}/schedules`, {
      cronExpression,
      parameters: workflowParameters,
      tenantCode,
      ...(configOverrides !== undefined && { configOverrides }),
    });
  }

  async updateWorkflowSchedule(scheduleId: string, updates: any): Promise<any> {
    return this.put(`/api/workflow-schedules/${encodeURIComponent(scheduleId)}`, updates);
  }

  async deleteWorkflowSchedule(scheduleId: string): Promise<any> {
    return this.delete(`/api/workflow-schedules/${encodeURIComponent(scheduleId)}`);
  }

  async refreshWorkflowDefinitions(): Promise<any> {
    return this.post('/api/workflows/refresh', {});
  }

  // Approval operations
  async getApprovals(filters?: { status?: string; type?: string }): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.type) params.append('type', filters.type);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.get(`/api/approvals${query}`);
  }

  async refreshApprovals(): Promise<any[]> {
    return this.post('/api/approvals/user/refresh', { mode: 'fast' });
  }

  async getApproval(id: string): Promise<any> {
    return this.get(`/api/approvals/${encodeURIComponent(id)}`);
  }

  async approveApproval(id: string, comment?: string): Promise<any> {
    return this.post(`/api/approvals/${encodeURIComponent(id)}/approve`, { comment });
  }

  async rejectApproval(id: string, comment?: string): Promise<any> {
    return this.post(`/api/approvals/${encodeURIComponent(id)}/reject`, { comment });
  }

  async getApprovalStats(): Promise<any> {
    return this.get('/api/approvals/stats');
  }

  // Approvals admin operations
  async getApprovalsConfig(): Promise<any> {
    return this.get('/api/approvals/config');
  }

  async getApprovalsStatus(): Promise<any> {
    return this.get('/api/approvals/status');
  }

  async testApprovalsConnection(): Promise<any> {
    return this.post('/api/approvals/test-connection');
  }

  async getApprovalsConsentUrl(): Promise<{ url: string | null }> {
    return this.get('/api/approvals/consent-url');
  }

  // Workflow Permissions
  async getWorkflowPermissions(): Promise<any> {
    return this.get('/api/workflow-permissions');
  }

  async getWorkflowPermission(workflowName: string): Promise<any> {
    return this.get(`/api/workflow-permissions/${encodeURIComponent(workflowName)}`);
  }

  async updateWorkflowPermission(
    workflowName: string,
    groupId: string,
    permission: { access: 'unconditional' | 'conditional' | 'view' | 'none'; approverPool?: 'unconditional' | 'conditional' }
  ): Promise<any> {
    return this.put(
      `/api/workflow-permissions/${encodeURIComponent(workflowName)}/groups/${encodeURIComponent(groupId)}`,
      permission
    );
  }

  async removeWorkflowPermission(workflowName: string, groupId: string): Promise<any> {
    return this.delete(
      `/api/workflow-permissions/${encodeURIComponent(workflowName)}/groups/${encodeURIComponent(groupId)}`
    );
  }

  async syncWorkflowPermissions(): Promise<any> {
    return this.post('/api/workflow-permissions/sync');
  }

  // CLI Permissions
  async getCliSchema(): Promise<any> {
    return this.get('/api/cli-permissions/schema');
  }

  async getCliCommands(): Promise<any> {
    return this.get('/api/cli-permissions/schema/commands');
  }

  async getAllCliPermissions(): Promise<any> {
    return this.get('/api/cli-permissions');
  }

  async getGroupCliPermissions(groupName: string): Promise<any> {
    return this.get(`/api/cli-permissions/${encodeURIComponent(groupName)}`);
  }

  async updateGroupCliPermissions(
    groupName: string,
    data: { description?: string; permissions: Record<string, any> }
  ): Promise<any> {
    return this.put(`/api/cli-permissions/${encodeURIComponent(groupName)}`, data);
  }

  async deleteGroupCliPermissions(groupName: string): Promise<any> {
    return this.delete(`/api/cli-permissions/${encodeURIComponent(groupName)}`);
  }

  async checkCliPermission(command: string): Promise<any> {
    return this.post('/api/cli-permissions/check', { command });
  }

  async syncCliSchema(): Promise<any> {
    return this.post('/api/cli-permissions/sync');
  }

  // Notification config endpoints
  async getNotificationsMasterConfig(): Promise<any> {
    return this.get('/api/notifications/config/master');
  }

  async updateNotificationsMasterConfig(notifications: any): Promise<any> {
    return this.put('/api/notifications/config/master', { notifications });
  }

  async getNotificationsTenantConfig(tenantCode: string): Promise<any> {
    return this.get(`/api/notifications/config/tenant/${encodeURIComponent(tenantCode)}`);
  }

  async updateNotificationsTenantConfig(tenantCode: string, data: { monitoring?: any; notifications?: any }): Promise<any> {
    return this.put(`/api/notifications/config/tenant/${encodeURIComponent(tenantCode)}`, data);
  }

  async getNotificationChannels(): Promise<{ channels: Record<string, string> }> {
    return this.get('/api/notifications/channels');
  }

  async getGraphApiStatus(tenantCode: string): Promise<any> {
    return this.get(`/api/notifications/graph-api-status/${encodeURIComponent(tenantCode)}`);
  }

  async requestGraphApiAccess(tenantCode: string, adminEmail: string, domain?: string): Promise<any> {
    return this.post(`/api/notifications/graph-api-request/${encodeURIComponent(tenantCode)}`, { adminEmail, domain });
  }

  async approveGraphApiAccess(
    tenantCode: string,
    credentials: { clientId: string; clientSecret: string; tenantId: string },
    domain?: string
  ): Promise<any> {
    return this.post(`/api/notifications/graph-api-approve/${encodeURIComponent(tenantCode)}`, { ...credentials, domain });
  }

  async getTenantsGraphApiStatus(): Promise<{ statuses: Record<string, any> }> {
    return this.get('/api/notifications/tenants-status');
  }

  async getTeamsInboxAuthStatus(): Promise<any> {
    return this.get('/api/notifications/teams-inbox/auth/status');
  }

  // Sent emails tracking endpoints
  async getSentEmailsMonths(): Promise<{ months: Array<{ year: number; month: number; label: string }> }> {
    return this.get('/api/notifications/emails/months');
  }

  async getSentEmails(year: number, month: number): Promise<{ emails: any[]; year: number; month: number }> {
    return this.get(`/api/notifications/emails?year=${year}&month=${month}`);
  }

  async getSentEmail(emailId: string, year?: number, month?: number): Promise<{ email: any }> {
    const params = new URLSearchParams();
    if (year) params.append('year', String(year));
    if (month) params.append('month', String(month));
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.get(`/api/notifications/emails/${encodeURIComponent(emailId)}${query}`);
  }

  // Default params descriptor (admin-only; OCI config/default-params.yaml)
  async getDefaultParams(): Promise<{ yaml: string }> {
    return this.get('/api/default-params');
  }

  async updateDefaultParams(yaml: string): Promise<any> {
    return this.put('/api/default-params', { yaml });
  }

  // Microsoft Graph domain registry (admin raw editor)
  async getMicrosoftGraphDomainsRaw(): Promise<{ yaml: string }> {
    return this.get('/api/microsoft-graph-domains/raw');
  }

  async saveMicrosoftGraphDomainsRaw(yaml: string): Promise<any> {
    return this.post('/api/microsoft-graph-domains/raw', { yaml });
  }

  // User notifications endpoints (Teams integration)
  async getUserNotifications(): Promise<any> {
    return this.get('/api/notifications/user');
  }

  async getUserNotificationsUnreadCount(): Promise<{ unreadCount: number }> {
    return this.get('/api/notifications/user/unread-count');
  }

  async markNotificationAsRead(notificationId: string, chatId: string): Promise<any> {
    return this.post(`/api/notifications/user/${encodeURIComponent(notificationId)}/mark-read`, { chatId });
  }

  async refreshUserNotifications(): Promise<any> {
    return this.post('/api/notifications/user/refresh', {});
  }

  async checkUserGraphApiAccess(): Promise<{ hasAccess: boolean }> {
    return this.get('/api/notifications/user/has-access');
  }

  // CI/CD endpoints
  async getCicdProducts(tenantCode?: string): Promise<{ products: any[] }> {
    const qs = tenantCode ? `?tenantCode=${encodeURIComponent(tenantCode)}` : '';
    return this.get(`/api/cicd/products${qs}`);
  }

  async getCicdProductsCatalog(tenantCodes?: string[]): Promise<{
    commonProducts: any[];
    tenantSections: Array<{ tenantCode: string; products: any[] }>;
  }> {
    const params = new URLSearchParams({ grouped: 'true' });
    if (tenantCodes?.length) {
      params.set('tenants', tenantCodes.join(','));
    }
    return this.get(`/api/cicd/products?${params.toString()}`);
  }

  async getCicdTenantProducts(tenantCode: string): Promise<{ tenantCode: string; products: any[] }> {
    return this.get(`/api/cicd/tenants/${encodeURIComponent(tenantCode)}/products`);
  }

  async createCicdProduct(product: any): Promise<any> {
    return this.post('/api/cicd/products', product);
  }

  async updateCicdProduct(id: string, updates: any): Promise<any> {
    return this.put(`/api/cicd/products/${encodeURIComponent(id)}`, updates);
  }

  async deleteCicdProduct(id: string): Promise<any> {
    return this.delete(`/api/cicd/products/${encodeURIComponent(id)}`);
  }

  async getCicdProductTenants(id: string): Promise<{ associations: any[] }> {
    return this.get(`/api/cicd/products/${encodeURIComponent(id)}/tenants`);
  }

  async associateCicdProductTenant(productId: string, tenantCode: string): Promise<any> {
    return this.post(`/api/cicd/products/${encodeURIComponent(productId)}/tenants/${encodeURIComponent(tenantCode)}`);
  }

  async disassociateCicdProductTenant(productId: string, tenantCode: string): Promise<any> {
    return this.delete(`/api/cicd/products/${encodeURIComponent(productId)}/tenants/${encodeURIComponent(tenantCode)}`);
  }

  async setCicdProductEnvConfig(productId: string, tenantCode: string, env: string, config: any): Promise<any> {
    return this.put(
      `/api/cicd/products/${encodeURIComponent(productId)}/tenants/${encodeURIComponent(tenantCode)}/envs/${encodeURIComponent(env)}`,
      config
    );
  }

  async removeCicdProductEnvConfig(productId: string, tenantCode: string, env: string): Promise<any> {
    return this.delete(
      `/api/cicd/products/${encodeURIComponent(productId)}/tenants/${encodeURIComponent(tenantCode)}/envs/${encodeURIComponent(env)}`
    );
  }

  async enableCicdProductWebhook(productId: string, options?: { webhookBaseUrl?: string; secret?: string }): Promise<any> {
    return this.post(`/api/cicd/products/${encodeURIComponent(productId)}/webhook/enable`, options || {});
  }

  async disableCicdProductWebhook(productId: string): Promise<any> {
    return this.post(`/api/cicd/products/${encodeURIComponent(productId)}/webhook/disable`);
  }

  async getCicdProductRuns(productId: string): Promise<{ runs: any[] }> {
    return this.get(`/api/cicd/products/${encodeURIComponent(productId)}/runs`);
  }

  async getCicdWebhookEvents(): Promise<{ events: any[] }> {
    return this.get('/api/cicd/webhook-events');
  }

  async deleteCicdWebhookEvent(id: string): Promise<{ ok: boolean }> {
    return this.delete(`/api/cicd/webhook-events/${encodeURIComponent(id)}`);
  }

  // AI workflows — streams, knowledge, evolution (AI Learning page)
  async getAiWorkflowStreams(): Promise<{ streams: any[] }> {
    return this.get('/api/ai-workflows/streams');
  }

  async getAiWorkflowKnowledge(streamId: string): Promise<{ entries: any[]; total: number }> {
    return this.get(`/api/ai-workflows/${encodeURIComponent(streamId)}/knowledge`);
  }

  async createAiWorkflowKnowledge(
    streamId: string,
    body: { content: string; type?: string; source?: string },
  ): Promise<{ success?: boolean; message?: string }> {
    return this.post(`/api/ai-workflows/${encodeURIComponent(streamId)}/knowledge`, body);
  }

  async updateAiWorkflowKnowledgeEntry(
    streamId: string,
    entryId: string,
    body: { status: 'approved' | 'rejected' },
  ): Promise<{ success?: boolean }> {
    return this.patch(
      `/api/ai-workflows/${encodeURIComponent(streamId)}/knowledge/${encodeURIComponent(entryId)}`,
      body,
    );
  }

  async getAiWorkflowEvolutionInsights(streamId: string): Promise<{ insights: any[] }> {
    return this.get(`/api/ai-workflows/${encodeURIComponent(streamId)}/evolution/insights`);
  }

  async getAiWorkflowEvolutionProposals(streamId: string): Promise<{ proposals: any[] }> {
    return this.get(`/api/ai-workflows/${encodeURIComponent(streamId)}/evolution/proposals`);
  }

  async updateAiWorkflowEvolutionProposal(
    streamId: string,
    proposalId: string,
    body: { status: 'approved' | 'rejected' },
  ): Promise<{ success?: boolean }> {
    return this.patch(
      `/api/ai-workflows/${encodeURIComponent(streamId)}/evolution/proposals/${encodeURIComponent(proposalId)}`,
      body,
    );
  }

  async getAiWorkflowActivity(params: { streamId?: string; limit?: number }): Promise<{ entries: any[]; total: number }> {
    const search = new URLSearchParams();
    if (params.streamId) search.set('streamId', params.streamId);
    if (params.limit != null) search.set('limit', String(params.limit));
    const qs = search.toString();
    return this.get(`/api/ai-workflows/activity${qs ? `?${qs}` : ''}`);
  }

  // AI Workflow Runs
  async getAiWorkflowRuns(params?: { streamId?: string; status?: string; limit?: number }): Promise<{ runs: any[]; total: number }> {
    const search = new URLSearchParams();
    if (params?.streamId) search.set('streamId', params.streamId);
    if (params?.status) search.set('status', params.status);
    if (params?.limit != null) search.set('limit', String(params.limit));
    const qs = search.toString();
    return this.get(`/api/ai-workflows/runs${qs ? `?${qs}` : ''}`);
  }

  async getAiWorkflowRun(runId: string): Promise<{ run: any }> {
    return this.get(`/api/ai-workflows/runs/${encodeURIComponent(runId)}`);
  }

  async getAiWorkflowRunDetail(runId: string) {
    return this.get<any>(`/api/ai-workflows/runs/${runId}/detail`);
  }

  async getAiWorkflowRunInspection(runId: string) {
    return this.get<any>(`/api/ai-workflows/runs/${runId}/inspection`);
  }

  async getAiWorkflowRunConversation(runId: string) {
    return this.get<any>(`/api/ai-workflows/runs/${runId}/conversation`);
  }

  async getAiWorkflowStageDetail(runId: string, stageKey: string) {
    return this.get<any>(`/api/ai-workflows/runs/${runId}/stages/${stageKey}`);
  }

  async getAiWorkflowHumanInputs(params?: { streamId?: string }) {
    const qs = params?.streamId ? `?streamId=${params.streamId}` : '';
    return this.get<any>(`/api/ai-workflows/human-inputs${qs}`);
  }

  async respondToHumanInput(contextId: string, questionId: string, answer: string) {
    return this.post<any>(`/api/ai-workflows/human-inputs/${contextId}/respond`, { questionId, answer });
  }

  async resumeAiWorkflowRun(runId: string, targetStage?: string, reason?: string) {
    return this.post<any>(`/api/ai-workflows/runs/${runId}/resume`, { targetStage, reason });
  }

  async deleteAiWorkflowRun(runId: string) {
    return this.delete<any>(`/api/ai-workflows/runs/${runId}`);
  }

  async deleteHumanInput(contextId: string) {
    return this.delete<any>(`/api/ai-workflows/human-inputs/${contextId}`);
  }

  // AI Planning Sessions
  async getAiWorkflowSessions(params?: { streamId?: string; status?: string }): Promise<{ sessions: any[] }> {
    const search = new URLSearchParams();
    if (params?.streamId) search.set('streamId', params.streamId);
    if (params?.status) search.set('status', params.status);
    const qs = search.toString();
    return this.get(`/api/ai-workflows/sessions${qs ? `?${qs}` : ''}`);
  }

  async interveneAiWorkflowSession(contextId: string, message: string): Promise<{ session: any }> {
    return this.post(`/api/ai-workflows/sessions/${encodeURIComponent(contextId)}/intervene`, { message });
  }

  private aiConfigPath(path: string, workspaceId?: string): string {
    const ws = encodeURIComponent(workspaceId || getActiveAiWorkspaceId());
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `/api/admin/ai-config/${ws}${normalizedPath}`;
  }

  // AI Workspaces
  async getAiWorkspaces(): Promise<{ workspaces: Array<{
    id: string;
    tenantCode: string;
    displayName: string;
    description?: string;
    color?: string;
    icon?: string;
    streamCount?: number;
    roleCount?: number;
    lastRunAt?: string | null;
    createdAt?: string;
    createdBy?: string;
    updatedAt?: string;
    updatedBy?: string;
  }> }> {
    return this.get('/api/admin/ai-workspaces');
  }

  async getAiWorkspace(id: string): Promise<{ workspace: {
    id: string;
    tenantCode: string;
    displayName: string;
    description?: string;
    color?: string;
    icon?: string;
    createdAt?: string;
    createdBy?: string;
    updatedAt?: string;
    updatedBy?: string;
  } }> {
    return this.get(`/api/admin/ai-workspaces/${encodeURIComponent(id)}`);
  }

  async createAiWorkspace(body: { id: string; tenantCode: string; displayName: string; description?: string; color?: string; icon?: string }): Promise<{ success: boolean; workspace: any }> {
    return this.post('/api/admin/ai-workspaces', body);
  }

  async updateAiWorkspace(id: string, body: { displayName?: string; description?: string; color?: string; icon?: string }): Promise<{ success: boolean; workspace: any }> {
    return this.put(`/api/admin/ai-workspaces/${encodeURIComponent(id)}`, body);
  }

  async deleteAiWorkspace(id: string): Promise<{ success: boolean }> {
    return this.delete(`/api/admin/ai-workspaces/${encodeURIComponent(id)}`);
  }

  // AI Config Admin
  async getAiConfigStreams(): Promise<{ streams: any[] }> {
    return this.get(this.aiConfigPath('/streams'));
  }

  async getAiConfigStreamYaml(streamId: string): Promise<{ streamId: string; yaml: string }> {
    return this.get(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}`));
  }

  async updateAiConfigStream(streamId: string, yaml: string): Promise<{ success: boolean }> {
    return this.put(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}`), { yaml });
  }

  async createAiConfigStream(streamId: string, yaml: string): Promise<{ success: boolean }> {
    return this.post(this.aiConfigPath('/streams'), { streamId, yaml });
  }

  async pauseAiConfigStream(streamId: string): Promise<{ success: boolean }> {
    return this.post(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/pause`), {});
  }

  async resumeAiConfigStream(streamId: string): Promise<{ success: boolean }> {
    return this.post(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/resume`), {});
  }

  async deleteAiConfigStream(streamId: string): Promise<void> {
    return this.delete(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}`));
  }

  async getAiConfigDeleteImpact(streamId: string, entityType: string, entityId: string): Promise<any> {
    return this.get(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/delete-impact?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`));
  }

  async getAiConfigTools(): Promise<{ tools: any[] }> {
    return this.get(this.aiConfigPath('/tools'));
  }

  async getAiIntegrations(): Promise<{ integrations: any[] }> {
    return this.get(this.aiConfigPath('/integrations'));
  }

  async upsertAiIntegration(integrationId: string, body: any): Promise<{ success: boolean }> {
    return this.put(this.aiConfigPath(`/integrations/${encodeURIComponent(integrationId)}`), body);
  }

  async getAiPlugins(): Promise<{ plugins: any[] }> {
    return this.get(this.aiConfigPath('/plugins'));
  }

  async upsertAiPlugin(pluginId: string, body: any): Promise<{ success: boolean }> {
    return this.put(this.aiConfigPath(`/plugins/${encodeURIComponent(pluginId)}`), body);
  }

  async getAiConfigToolOptions(
    toolId: string,
    query?: { tenantCodes?: string[] },
  ): Promise<{ toolId: string; options: any }> {
    const params = new URLSearchParams();
    if (query?.tenantCodes?.length) params.set('tenantCodes', query.tenantCodes.join(','));
    const qs = params.toString();
    return this.get(this.aiConfigPath(`/tools/${encodeURIComponent(toolId)}/options${qs ? `?${qs}` : ''}`));
  }

  async getAiStreamSyncState(streamId: string): Promise<{ streamId: string; states: any[] }> {
    return this.get(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/sync-state`));
  }

  async previewAiRoleSync(streamId: string, roleCode: string): Promise<any> {
    return this.get(this.aiConfigPath(
      `/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/sync/preview`,
    ), { timeoutMs: 300_000 });
  }

  async syncAiRole(streamId: string, roleCode: string): Promise<any> {
    return this.post(this.aiConfigPath(
      `/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/sync`,
    ), {}, { timeoutMs: 300_000 });
  }

  async listCloudAgentEnvironments(): Promise<{ environments: Array<{
    streamId: string;
    roleCode: string;
    roleTitle: string;
    repository: string;
    toolIds: string[];
    status: 'ready' | 'missing' | 'stale' | 'error';
    error?: string;
  }> }> {
    return this.get(this.aiConfigPath('/cloud-environments'));
  }

  async setupCloudAgentEnvironments(roles: Array<{ streamId: string; roleCode: string }>): Promise<{
    results: Array<{ streamId: string; roleCode: string; repository: string; changedFiles: number; error?: string }>;
    environments: Array<{
      streamId: string;
      roleCode: string;
      roleTitle: string;
      repository: string;
      toolIds: string[];
      status: 'ready' | 'missing' | 'stale' | 'error';
      error?: string;
    }>;
  }> {
    return this.post(this.aiConfigPath('/cloud-environments/setup'), { roles });
  }

  async syncAiStream(streamId: string, roleCodes?: string[]): Promise<any> {
    return this.post(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/sync`), {
      ...(roleCodes?.length ? { roleCodes } : {}),
    }, { timeoutMs: 300_000 });
  }

  async previewAiConfigResolve(body: { streamId: string; entityType: string; entityId: string; draftConfig: any }): Promise<{
    overlay: any;
    replace: any;
    effectiveToolsByWorkflowType?: { overlay: Record<string, any[]>; replace: Record<string, any[]> };
  }> {
    return this.post(this.aiConfigPath('/preview-resolve'), body);
  }

  async addAiConfigProduct(streamId: string, body: any): Promise<{ success: boolean }> {
    return this.post(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/products`), body);
  }

  async updateAiConfigProduct(streamId: string, id: string, body: any): Promise<{ success: boolean }> {
    return this.put(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/products/${encodeURIComponent(id)}`), body);
  }

  async deleteAiConfigProduct(streamId: string, id: string): Promise<{ success: boolean }> {
    return this.delete(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/products/${encodeURIComponent(id)}`));
  }

  async addAiConfigTeam(streamId: string, body: any): Promise<{ success: boolean }> {
    return this.post(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/teams`), body);
  }

  async updateAiConfigTeam(streamId: string, id: string, body: any): Promise<{ success: boolean }> {
    return this.put(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/teams/${encodeURIComponent(id)}`), body);
  }

  async deleteAiConfigTeam(streamId: string, id: string): Promise<{ success: boolean }> {
    return this.delete(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/teams/${encodeURIComponent(id)}`));
  }

  async addAiConfigTenant(streamId: string, body: any): Promise<{ success: boolean }> {
    return this.post(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/tenants`), body);
  }

  async updateAiConfigTenant(streamId: string, code: string, body: any): Promise<{ success: boolean }> {
    return this.put(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/tenants/${encodeURIComponent(code)}`), body);
  }

  async deleteAiConfigTenant(streamId: string, code: string): Promise<{ success: boolean }> {
    return this.delete(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/tenants/${encodeURIComponent(code)}`));
  }

  async addAiConfigRole(streamId: string, body: any): Promise<{ success: boolean }> {
    return this.post(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles`), body);
  }

  async updateAiConfigRole(streamId: string, code: string, body: any): Promise<{ success: boolean }> {
    return this.put(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(code)}`), body);
  }

  async deleteAiConfigRole(streamId: string, code: string): Promise<{ success: boolean }> {
    return this.delete(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(code)}`));
  }

  async getAiConfigDefaultsParsed(): Promise<{ defaults: any }> {
    return this.get(this.aiConfigPath('/defaults/parsed'));
  }

  // AI Onboarding
  async onboardAiTenant(body: any): Promise<any> {
    return this.post('/api/admin/ai-onboarding/tenant', body);
  }

  async validateAiOnboarding(body: any): Promise<any> {
    return this.post('/api/admin/ai-onboarding/tenant/validate', body);
  }

  async getAiOnboardingTemplates(): Promise<{ templates: any[] }> {
    return this.get('/api/admin/ai-onboarding/templates');
  }

  // AI Knowledge / Learning
  async getAiKnowledgeEntries(streamId: string, params?: { type?: string; status?: string; limit?: number }): Promise<{ entries: any[]; total: number }> {
    const search = new URLSearchParams();
    if (params?.type) search.set('type', params.type);
    if (params?.status) search.set('status', params.status);
    if (params?.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    return this.get(`/api/ai-workflows/${encodeURIComponent(streamId)}/knowledge${qs ? `?${qs}` : ''}`);
  }

  async getAiKnowledgeEntry(streamId: string, entryId: string): Promise<{ entry: any }> {
    return this.get(`/api/ai-workflows/${encodeURIComponent(streamId)}/knowledge/${encodeURIComponent(entryId)}`);
  }

  async createAiKnowledgeEntry(streamId: string, body: { content: string; type?: string; workflowType?: string; repository?: string; links?: string[] }): Promise<{ success: boolean; entry: any }> {
    return this.post(`/api/ai-workflows/${encodeURIComponent(streamId)}/knowledge`, body);
  }

  async updateAiKnowledgeEntryStatus(streamId: string, entryId: string, status: string): Promise<{ success: boolean }> {
    return this.patch(`/api/ai-workflows/${encodeURIComponent(streamId)}/knowledge/${encodeURIComponent(entryId)}`, { status });
  }

  // AI Evolution
  async getAiEvolutionInsights(streamId: string): Promise<{ insights: any[] }> {
    return this.get(`/api/ai-workflows/${encodeURIComponent(streamId)}/evolution/insights`);
  }

  async getAiEvolutionProposals(streamId: string): Promise<{ proposals: any[] }> {
    return this.get(`/api/ai-workflows/${encodeURIComponent(streamId)}/evolution/proposals`);
  }

  async approveAiEvolutionProposal(streamId: string, proposalId: string): Promise<{ success: boolean }> {
    return this.post(`/api/ai-workflows/${encodeURIComponent(streamId)}/evolution/proposals/${encodeURIComponent(proposalId)}/approve`, {});
  }

  async rejectAiEvolutionProposal(streamId: string, proposalId: string): Promise<{ success: boolean }> {
    return this.post(`/api/ai-workflows/${encodeURIComponent(streamId)}/evolution/proposals/${encodeURIComponent(proposalId)}/reject`, {});
  }

  async triggerAiEvolutionAnalysis(streamId: string): Promise<{ success: boolean; outcomeCount: number }> {
    return this.post(`/api/ai-workflows/${encodeURIComponent(streamId)}/evolution/analyze`, {});
  }

  // AI Workflow Costs
  async getAiWorkflowCosts(params?: { streamId?: string; from?: string; to?: string }): Promise<{ totalTokensInput: number; totalTokensOutput: number; totalCostUsd: number; runCount: number }> {
    const search = new URLSearchParams();
    if (params?.streamId) search.set('streamId', params.streamId);
    if (params?.from) search.set('from', params.from);
    if (params?.to) search.set('to', params.to);
    const qs = search.toString();
    return this.get(`/api/ai-workflows/costs${qs ? `?${qs}` : ''}`);
  }

  // AI Stream Schedules
  async getAiStreamSchedules(streamId: string): Promise<{ schedules: any[] }> {
    return this.get(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/schedules`));
  }

  async createAiStreamSchedule(streamId: string, body: { schedule: string; workflowName: string; parameters?: Record<string, any> }): Promise<any> {
    return this.post(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/schedules`), body);
  }

  async updateAiStreamSchedule(
    streamId: string,
    scheduleId: string,
    body: { enabled?: boolean; schedule?: string; parameters?: Record<string, any> },
  ): Promise<any> {
    return this.put(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/schedules/${encodeURIComponent(scheduleId)}`), body);
  }

  async deleteAiStreamSchedule(streamId: string, scheduleId: string): Promise<void> {
    return this.delete(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/schedules/${encodeURIComponent(scheduleId)}`));
  }

  async previewReconciliation(body: {
    streamId: string;
    entityType: 'role' | 'stream';
    entityId: string;
    patch?: any;
    yaml?: string;
  }): Promise<{ proposedSteps: any[] }> {
    return this.post(this.aiConfigPath('/reconciliation/preview'), body);
  }

  async executeReconciliation(body: {
    streamId: string;
    entityType: 'role' | 'stream';
    entityId: string;
    patch?: any;
    yaml?: string;
    mode?: 'catchup' | 'deprovision';
    approvedStepIds: string[];
  }): Promise<{ success: boolean; steps: any[] }> {
    return this.post(this.aiConfigPath('/reconciliation/execute'), body);
  }

  async streamExecuteReconciliation(
    body: {
      streamId: string;
      entityType: 'role' | 'stream';
      entityId: string;
      patch?: any;
      yaml?: string;
      mode?: 'catchup' | 'deprovision';
      approvedStepIds: string[];
    },
    onEvent: (event: any) => void,
  ): Promise<void> {
    return this.postStream(this.aiConfigPath('/reconciliation/execute-stream'), body, onEvent);
  }

  async auditReconciliation(streamId: string): Promise<{ discrepancies: any[] }> {
    return this.get(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/reconciliation/audit`),
      { timeoutMs: 300_000 },
    );
  }

  async previewStreamCatchUpReconciliation(streamId: string): Promise<{ proposedSteps: any[] }> {
    return this.post(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/reconciliation/preview-catchup`),
      {},
      { timeoutMs: 300_000 },
    );
  }

  async auditRoleResources(
    streamId: string,
    roleCode: string,
  ): Promise<{ discrepancies: any[] }> {
    return this.get(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/reconciliation/audit`),
      { timeoutMs: 180_000 },
    );
  }

  async previewRoleCatchUpReconciliation(
    streamId: string,
    roleCode: string,
  ): Promise<{ proposedSteps: any[] }> {
    return this.post(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/reconciliation/preview-catchup`),
      {},
      { timeoutMs: 180_000 },
    );
  }

  async previewRoleDeprovisionReconciliation(
    streamId: string,
    roleCode: string,
  ): Promise<{ proposedSteps: any[] }> {
    return this.post(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/reconciliation/preview-deprovision`),
      {},
      { timeoutMs: 180_000 },
    );
  }

  async listRoleBotUsers(
    streamId: string,
    roleCode: string,
  ): Promise<{
    supported: boolean;
    warning?: string;
    users: Array<{ userId: string; displayName: string; email: string; installed: boolean }>;
  }> {
    return this.get(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/bot/users`),
      { timeoutMs: 180_000 },
    );
  }

  async installRoleBotForUsers(
    streamId: string,
    roleCode: string,
    userIdentifiers: string[],
  ): Promise<{ supported: boolean; installed: number }> {
    return this.post(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/bot/users/install`),
      { userIdentifiers },
    );
  }

  async streamInstallRoleBotForUsers(
    streamId: string,
    roleCode: string,
    userIdentifiers: string[],
    onEvent: (event: any) => void,
  ): Promise<void> {
    return this.postStream(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/bot/users/install-stream`),
      { userIdentifiers },
      onEvent,
    );
  }

  async uninstallRoleBotForUsers(
    streamId: string,
    roleCode: string,
    userIdentifiers: string[],
  ): Promise<{ supported: boolean; removed: number }> {
    return this.post(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/bot/users/uninstall`),
      { userIdentifiers },
    );
  }

  async streamUninstallRoleBotForUsers(
    streamId: string,
    roleCode: string,
    userIdentifiers: string[],
    onEvent: (event: any) => void,
  ): Promise<void> {
    return this.postStream(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/bot/users/uninstall-stream`),
      { userIdentifiers },
      onEvent,
    );
  }

  // Draft Mode
  async listDraftSessions(): Promise<{
    sessions: Array<{
      id: string;
      title: string;
      userId: string;
      status: string;
      streamCount: number;
      messageCount: number;
      createdAt: string;
    }>;
  }> {
    return this.get(this.aiConfigPath('/draft/sessions'));
  }

  async deleteDraftSession(id: string): Promise<{ success: boolean }> {
    return this.delete(this.aiConfigPath(`/draft/sessions/${encodeURIComponent(id)}`));
  }

  async startDraftSession(streamIds?: string[]): Promise<{
    sessionId: string;
    greeting: string;
    draftYamls: Record<string, string>;
    status: string;
  }> {
    return this.post(this.aiConfigPath('/draft/start'), { streamIds });
  }

  async sendDraftMessage(
    sessionId: string,
    message: string,
    options?: { model?: string; cursorEffort?: string; signal?: AbortSignal },
  ): Promise<{
    content: string;
    draftYamls: Record<string, string>;
    synergyPlanMd: string | null;
    status: string;
  }> {
    return this.post(
      this.aiConfigPath('/draft/message'),
      {
        sessionId,
        message,
        model: options?.model,
        cursorEffort: options?.cursorEffort,
      },
      { timeoutMs: 600_000, signal: options?.signal },
    );
  }

  async streamDraftMessage(
    sessionId: string,
    message: string,
    options: { model?: string; cursorEffort?: string },
    onEvent: (event: any) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    return this.postStream(
      this.aiConfigPath('/draft/message/stream'),
      {
        sessionId,
        message,
        model: options.model,
        cursorEffort: options.cursorEffort,
      },
      onEvent,
      signal,
    );
  }

  async getDraftModels(): Promise<{
    cursor: {
      models: Array<{
        id: string;
        label?: string;
        levels?: Array<{ id: string; label: string; params: Array<{ id: string; value: string }> }>;
      }>;
    };
  }> {
    return this.get(this.aiConfigPath('/draft/models'));
  }

  async previewOrgSetupSync(): Promise<any> {
    return this.get(this.aiConfigPath('/org-setup/sync/preview'));
  }

  async syncOrgSetupTrainer(): Promise<any> {
    return this.post(this.aiConfigPath('/org-setup/sync'), {});
  }

  async getDraftSession(id: string): Promise<{
    id: string;
    seedStreamIds: string[];
    messages: Array<{ role: 'user' | 'assistant'; content: string }>;
    draftYamls: Record<string, string>;
    synergyPlanMd: string | null;
    status: string;
    agentRunState?: 'idle' | 'running';
  }> {
    return this.get(this.aiConfigPath(`/draft/sessions/${encodeURIComponent(id)}`));
  }

  async generateSynergyPlan(sessionId: string): Promise<{ markdown: string }> {
    return this.post(
      this.aiConfigPath(`/draft/sessions/${encodeURIComponent(sessionId)}/synergy-plan`),
      undefined,
      { timeoutMs: 600_000 },
    );
  }

  async saveDraftSynergyPlan(sessionId: string, markdown: string): Promise<{ success: boolean; markdown: string }> {
    return this.put(this.aiConfigPath(`/draft/sessions/${encodeURIComponent(sessionId)}/synergy-plan`), { markdown });
  }

  async getDraftReconciliationPreview(sessionId: string): Promise<{
    streams: Array<{
      streamId: string;
      displayName: string;
      isNew: boolean;
      steps: Array<{
        id: string;
        action: 'create' | 'update' | 'delete';
        resourceType: string;
        resourceName: string;
        roleCode: string;
        streamId: string;
        targetSystem: string;
        reason: string;
        defaultSelected: boolean;
      }>;
    }>;
  }> {
    return this.post(this.aiConfigPath(`/draft/sessions/${encodeURIComponent(sessionId)}/reconciliation-preview`));
  }

  async applyDraftSession(sessionId: string, approvedStepIds?: string[]): Promise<{
    success: boolean;
    results: Array<{ streamId: string; status: 'written' | 'failed'; error?: string }>;
  }> {
    return this.post(
      this.aiConfigPath(`/draft/sessions/${encodeURIComponent(sessionId)}/apply`),
      { approvedStepIds },
      { timeoutMs: 300_000 },
    );
  }

  /** AI Org Setup — per-role portal chat (Redis-backed; workspace-scoped via aiConfigPath) */
  async createAiRoleChat(
    streamId: string,
    roleCode: string,
    body?: { title?: string; visibility?: 'private' | 'workspace' | 'shared'; sharedWith?: string[] },
  ): Promise<{ chat: Record<string, unknown> }> {
    return this.post(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/chats`),
      body || {},
    );
  }

  async listAiRoleChats(
    streamId: string,
    roleCode: string,
    tab: 'mine' | 'workspace' | 'shared',
  ): Promise<{ chats: Record<string, unknown>[] }> {
    const qs = `tab=${encodeURIComponent(tab)}`;
    return this.get(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/chats?${qs}`),
    );
  }

  async getAiRoleChat(chatId: string): Promise<{
    chat: Record<string, unknown>;
    messages: Array<{
      role: string;
      content: string;
      ts: string;
      provider?: 'openai-agents' | 'cursor-cloud-agent';
      attachments?: Array<{
        id: string;
        kind: 'text' | 'image';
        name: string;
        mimeType: string;
        size: number;
        content?: string;
        data?: string;
      }>;
      cursorEvents?: Array<Record<string, unknown>>;
    }>;
    olderCount?: number;
    activeRuns?: Array<{
      runId: string;
      status: string;
      channel?: string;
      provider?: string;
      source?: string;
      parentRoleCode?: string;
      parentChatId?: string;
      parentRunId?: string;
      model?: string;
      effort?: string;
      assistantText?: string;
      error?: string;
      createdAt: string;
      updatedAt: string;
      finishedAt?: string;
    }>;
  }> {
    return this.get(this.aiConfigPath(`/chats/${encodeURIComponent(chatId)}`));
  }

  async sendAiRoleChatMessage(
    chatId: string,
    content: string,
    options?: {
      model?: string;
      reasoningEffort?: 'low' | 'medium' | 'high' | 'xhigh';
      provider?: 'openai' | 'cursor';
      cursorModel?: string;
      cursorEffort?: string;
      attachments?: Array<{
        id: string;
        kind: 'text' | 'image';
        name: string;
        mimeType: string;
        size: number;
        content?: string;
        data?: string;
      }>;
    },
  ): Promise<{
    message: string;
    chat: Record<string, unknown>;
    inspection?: unknown;
  }> {
    return this.post(
      this.aiConfigPath(`/chats/${encodeURIComponent(chatId)}/messages`),
      {
        content,
        model: options?.model,
        reasoningEffort: options?.reasoningEffort,
        provider: options?.provider,
        cursorModel: options?.cursorModel,
        cursorEffort: options?.cursorEffort,
        attachments: options?.attachments,
      },
      { timeoutMs: 120_000 },
    );
  }

  async streamAiRoleChatMessage(
    chatId: string,
    content: string,
    options: {
      provider: 'cursor';
      cursorModel?: string;
      cursorEffort?: string;
      attachments?: Array<{
        id: string;
        kind: 'text' | 'image';
        name: string;
        mimeType: string;
        size: number;
        content?: string;
        data?: string;
      }>;
    },
    onEvent: (event: any) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    return this.postStream(
      this.aiConfigPath(`/chats/${encodeURIComponent(chatId)}/messages/stream`),
      {
        content,
        provider: options.provider,
        cursorModel: options.cursorModel,
        cursorEffort: options.cursorEffort,
        attachments: options.attachments,
      },
      onEvent,
      signal,
    );
  }

  async streamAiRoleChatRun(
    chatId: string,
    runId: string,
    onEvent: (event: any) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    return this.getStream(
      this.aiConfigPath(`/chats/${encodeURIComponent(chatId)}/runs/${encodeURIComponent(runId)}/stream`),
      onEvent,
      signal,
    );
  }

  async getAiRoleChatModels(
    streamId: string,
    roleCode: string,
  ): Promise<{
    openai: { model: string; efforts: Array<'low' | 'medium' | 'high' | 'xhigh'> };
    cursor: {
      models: Array<{
        id: string;
        label?: string;
        levels?: Array<{ id: string; label: string; params: Array<{ id: string; value: string }> }>;
        efforts?: Array<'low' | 'medium' | 'high' | 'xhigh'>;
      }>;
    };
  }> {
    return this.get(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/chat-models`),
    );
  }

  async patchAiRoleChat(
    chatId: string,
    body: { title?: string; visibility?: 'private' | 'workspace' | 'shared'; sharedWith?: string[] },
  ): Promise<{ chat: Record<string, unknown> }> {
    return this.patch(this.aiConfigPath(`/chats/${encodeURIComponent(chatId)}`), body);
  }

  /** Trigger an ai-pipeline workflow run from a specific assistant turn in a per-role chat. */
  async runAiRoleChatWorkflow(
    chatId: string,
    messageIndex: number,
    options?: { stepExecution?: 'independent_agents' | 'same_chat_followups' },
  ): Promise<{ runId: string; workflowName: string; status: string }> {
    return this.post(
      this.aiConfigPath(
        `/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(String(messageIndex))}/run-workflow`,
      ),
      options?.stepExecution ? { stepExecution: options.stepExecution } : {},
      { timeoutMs: 60_000 },
    );
  }

  async deleteAiRoleChat(chatId: string): Promise<{ success: boolean }> {
    return this.delete(this.aiConfigPath(`/chats/${encodeURIComponent(chatId)}`));
  }

  async streamAiRoleMission(
    streamId: string,
    body: {
      roleCode: string;
      content: string;
      chatId?: string;
      cursorModel?: string;
      cursorEffort?: string;
    },
    onEvent: (event: any) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    return this.postStream(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/missions`),
      {
        ...body,
        runInBackground: false,
      },
      onEvent,
      signal,
    );
  }

  async startAiRoleMissionBackground(
    streamId: string,
    body: {
      roleCode: string;
      content: string;
      chatId?: string;
      cursorModel?: string;
      cursorEffort?: string;
    },
  ): Promise<{ missionId: string; status: string }> {
    return this.post(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/missions`),
      {
        ...body,
        runInBackground: true,
      },
      { timeoutMs: 120_000 },
    );
  }

  async getAiMission(missionId: string): Promise<{
    mission: Record<string, unknown>;
    nodes: Array<Record<string, unknown>>;
    events: Array<Record<string, unknown>>;
  }> {
    return this.get(this.aiConfigPath(`/missions/${encodeURIComponent(missionId)}`));
  }

  async getAiChatMission(chatId: string): Promise<{
    mission: Record<string, unknown> | null;
    nodes: Array<Record<string, unknown>>;
    events: Array<Record<string, unknown>>;
  }> {
    return this.get(this.aiConfigPath(`/chats/${encodeURIComponent(chatId)}/mission`), { timeoutMs: 8_000 });
  }

  async streamAiMission(
    missionId: string,
    onEvent: (event: any) => void,
    signal?: AbortSignal,
    fromEventIndex?: number,
  ): Promise<void> {
    const query = fromEventIndex != null ? `?from=${encodeURIComponent(String(fromEventIndex))}` : '';
    return this.getStream(
      this.aiConfigPath(`/missions/${encodeURIComponent(missionId)}/stream${query}`),
      onEvent,
      signal,
    );
  }

  async getAiActiveAgents(streamId: string): Promise<{
    roles: Record<string, { working: boolean; activeChats: number; activeMissions: number }>;
  }> {
    return this.get(this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/active-agents`));
  }

  async getAiRoleActiveChats(
    streamId: string,
    roleCode: string,
  ): Promise<{
    items: Array<{
      kind: 'chat' | 'mission';
      chatId?: string;
      runId?: string;
      missionId?: string;
      title: string;
      startedAt: string;
      status: string;
      source?: string;
      parentRoleCode?: string;
    }>;
  }> {
    return this.get(
      this.aiConfigPath(`/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/active-chats`),
    );
  }

  listClusterBrokers(): Promise<{ registrations: ClusterBrokerRegistration[] }> {
    return this.request('/api/cluster-brokers/registrations');
  }

  createClusterBroker(input: {
    tenant: string;
    environment: string;
    clusterName: string;
    profile: string;
    clusterClassification: string;
  }): Promise<ClusterBrokerRegistration> {
    return this.request('/api/cluster-brokers/registrations', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  revokeClusterBroker(id: string): Promise<{ status: string }> {
    return this.request(`/api/cluster-brokers/registrations/${encodeURIComponent(id)}/revoke`, { method: 'POST' });
  }

  rotateClusterBroker(id: string): Promise<ClusterBrokerRegistration> {
    return this.request(`/api/cluster-brokers/registrations/${encodeURIComponent(id)}/rotate-identity`, { method: 'POST' });
  }
}

export interface ClusterBrokerRegistration {
  id: string;
  tenant: string;
  environment: string;
  clusterName: string;
  profile: string;
  clusterClassification: string;
  status: string;
  sessionEpoch?: number;
  imageVersion?: string;
  protocolVersion?: number;
  lastSeen?: string;
  lastError?: string;
  capabilities?: string[];
  metadata?: Record<string, string>;
  bootstrapToken?: string;
  expiresAt?: string;
}

export const apiClient = new ApiClient();




